import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { listMembers, deleteMember, toggleVip } from '../../services/adminService';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import { Star, UserPlus } from 'lucide-react';
import '../../styles/MembersList.css';

export default function MembersList() {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listMembers({ page, search });
      if (res.success) {
        setMembers(res.data);
        setPagination(res.pagination);
      }
    } catch { setError('Erreur lors du chargement'); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const handleDeleteVisitor = async (id) => {
    if (!window.confirm('Supprimer ce visiteur et toutes ses données ? (RGPD — irréversible)')) return;
    try {
      await deleteMember(id);
      setSuccess('Visiteur supprimé');
      fetchMembers();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erreur');
    }
  };

  const handleToggleVip = async (id, currentVip) => {
    try {
      await toggleVip(id, !currentVip);
      setMembers(prev => prev.map(m => m.id === id ? { ...m, is_vip: !currentVip } : m));
    } catch {
      setError('Erreur lors de la mise à jour VIP');
    }
  };

  return (
    <div className="members-page">
      <div className="members-header">
        <h1>Membres / Visiteurs</h1>
        <div className="members-header-actions">
          <input
            type="text"
            className="search-input"
            placeholder="Rechercher par nom ou email..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          <button
            className="btn btn-primary btn-add-visitor"
            onClick={() => navigate('/admin/members/new')}
            title="Inscrire un nouveau visiteur"
          >
            <UserPlus size={16} />
            Nouveau visiteur
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {success && <div className="success-banner">{success}</div>}

      {loading ? (
        <LoadingSkeleton lines={10} />
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Email</th>
                <th>Téléphone</th>
                <th>VIP</th>
                <th>Visites</th>
                <th>Dernière visite</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.length === 0 ? (
                <tr><td colSpan={7} className="no-data">Aucun résultat</td></tr>
              ) : members.map(m => (
                <tr key={m.id}>
                  <td>
                    {m.full_name}
                    {m.is_vip && <span className="vip-badge" title="VIP">★ VIP</span>}
                  </td>
                  <td>{m.email}</td>
                  <td>{m.phone}</td>
                  <td>
                    <button
                      className={`btn btn-sm ${m.is_vip ? 'btn-vip-active' : 'btn-vip'}`}
                      onClick={() => handleToggleVip(m.id, m.is_vip)}
                      title={m.is_vip ? 'Retirer VIP' : 'Marquer VIP'}
                    >
                      <Star size={14} fill={m.is_vip ? 'currentColor' : 'none'} />
                    </button>
                  </td>
                  <td>{m.visit_count || 0}</td>
                  <td>{m.last_visit ? new Date(m.last_visit).toLocaleDateString('fr-FR') : '—'}</td>
                  <td className="actions-cell">
                    <button className="btn btn-sm btn-danger" onClick={() => handleDeleteVisitor(m.id)} title="Supprimer (RGPD)">
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {pagination && pagination.totalPages > 1 && (
            <div className="pagination">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)}>← Précédent</button>
              <span>Page {page} / {pagination.totalPages}</span>
              <button disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>Suivant →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
